import { useDebounce } from '@uidotdev/usehooks'
import { SortingState } from '@tanstack/react-table'

import { useQuery } from '@/hooks'
import { DlInstance } from '@/lib/axios'
import { DataResponse, PaginatedResponse } from '@/models/api'
import { Bill } from '@/models/bill/bill'
import { datatableSortingStateToQuery } from '@/utils/api'
import { getSavedPdfFontScale } from '@/lib/fontScale'

type LiffOutstandingListParams = {
  page?: number
  pageSize?: number
  sort?: SortingState
  search?: string
  bill_type?: 'buy' | 'sell'
  location?: string
  enabled?: boolean
}

export const useGetLiffOutstandingBills = ({
  page = 1,
  pageSize = 20,
  sort,
  search,
  bill_type,
  location,
  enabled = true,
}: LiffOutstandingListParams) => {
  return useQuery<PaginatedResponse<Bill>>({
    queryKey: ['liff_outstanding_bills', { page, pageSize, sort, search, bill_type, location }],
    url: '/liff/outstanding/bills',
    axiosConfig: {
      params: {
        page,
        pageSize,
        search,
        bill_type,
        location,
        sort: datatableSortingStateToQuery(sort),
      },
    },
    queryConfig: { enabled },
  })
}

export const useGetLiffOutstandingTotal = (params: Omit<LiffOutstandingListParams, 'page' | 'pageSize'>) => {
  const debouncedSearch = useDebounce(params.search, 400)
  return useQuery<
    DataResponse<{
      total_bill: number
      total_quantity: number
      total_price: number
      total_remaining?: number
    }>
  >({
    queryKey: [
      'liff_outstanding_total',
      { search: debouncedSearch, bill_type: params.bill_type, location: params.location },
    ],
    url: '/liff/outstanding/bills/total-report',
    axiosConfig: {
      params: {
        search: debouncedSearch,
        bill_type: params.bill_type,
        location: params.location,
      },
    },
    queryConfig: { enabled: params.enabled !== false },
  })
}

export const useGetLiffOutstandingBill = (id: string | undefined) => {
  return useQuery<Bill>({
    queryKey: ['liff_outstanding_bill', id],
    url: `/liff/outstanding/bills/${id}`,
    queryConfig: {
      enabled: !!id,
      select: (payload: { data?: Bill } & Bill) => {
        if (payload && typeof payload === 'object' && 'data' in payload && payload.data) {
          return payload.data as Bill
        }
        return payload as unknown as Bill
      },
    },
  })
}

function openPdfBlobForPrint(blob: Blob) {
  const url = URL.createObjectURL(blob)
  const printWindow = window.open(url, '_blank')
  if (printWindow) {
    printWindow.onload = () => {
      printWindow.print()
    }
  }
}

export async function printLiffOutstandingBillPdf(billId: string, paymentRoundNo?: number) {
  const pdfFontScale = getSavedPdfFontScale()
  const params: Record<string, string | number> = {}
  if (paymentRoundNo != null && Number.isFinite(paymentRoundNo)) {
    params.payment_round = paymentRoundNo
  }
  params.pdf_font_scale = pdfFontScale
  const res = await DlInstance.get<Blob>(`/liff/outstanding/bills/${billId}/pdf`, {
    params,
    responseType: 'blob',
  })
  const blob = res.data
  if (blob instanceof Blob) {
    openPdfBlobForPrint(blob)
  }
}
